import { useMutation, useQueryClient } from '@tanstack/react-query';

import type {
  BulkResponse,
  BulkResultItem,
  BulkResultItemCode,
  CreateProductScheduleRequest,
  ProductSchedule,
  ProductScheduleChange,
  ScheduleOnEndInput,
  UpdateProductScheduleRequest,
} from '@nucleus-commerce/api-client/admin';
import { isApiError } from '@nucleus-commerce/api-client/transport';

import { schedulesApi } from '../api/schedules-api';

import { refreshSchedule, rememberSchedule } from './invalidate';
import { scheduleKeys } from './schedule-keys';

const withoutRelease = ({
  releaseRunId: _r,
  ...schedule
}: ProductScheduleChange): ProductSchedule => schedule;

export interface CreateScheduleInput {
  /** `productIds` are the hand-picked, unpinned members. */
  body: CreateProductScheduleRequest;
  /** Members that are live in every target store on every run, added after the create. */
  pinnedIds: readonly string[];
}

export interface CreateScheduleResult {
  schedule: ProductSchedule;
  /** Outcome of the add after the create (the pinned members). */
  added: BulkResponse[];
  /**
   * Every product the add left out, one row each: refused per id (a 207 row, `SCHEDULE_CONFLICT`
   * with the owner in `meta.scheduleId` for one another schedule owns), or in an add the backend
   * refused whole (that call's code and meta).
   */
  notAdded: BulkResultItem[];
}

const refusedRows = (response: BulkResponse) => response.items.filter((item) => item.status >= 300);

/** One row per id of an add the backend refused whole. */
function callRows(productIds: readonly string[], error: unknown): BulkResultItem[] {
  const api = isApiError(error) ? error : null;
  return productIds.map((id) => ({
    id,
    status: api?.status ?? 500,
    code: (api?.code ?? 'INTERNAL_ERROR') as BulkResultItemCode,
    message: api?.message ?? null,
    meta: api ? { ...api.meta } : {},
  }));
}

/**
 * Creates the draft schedule with its unpinned products (all or nothing, up to 5,000), then adds
 * the pinned ones in one members call, which answers per id: one another schedule owns comes back
 * as a `SCHEDULE_CONFLICT` row (owner in `meta.scheduleId`). The schedule exists once the create
 * answers, so a failing add never throws: what it left out comes back in `notAdded`.
 */
export function useCreateSchedule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ body, pinnedIds }: CreateScheduleInput): Promise<CreateScheduleResult> => {
      // An id list joins all or nothing, so there is no `memberResult` to read.
      const { memberResult: _result, ...schedule } = await schedulesApi.create(body);
      if (pinnedIds.length === 0) return { schedule, added: [], notAdded: [] };
      try {
        const response = await schedulesApi.addMembers(schedule.id, {
          productIds: [...pinnedIds],
          pinned: true,
        });
        return { schedule, added: [response], notAdded: refusedRows(response) };
      } catch (error) {
        return { schedule, added: [], notAdded: callRows(pinnedIds, error) };
      }
    },
    meta: { successMessage: 'schedules:toasts.created', silentError: true },
    onSuccess: async ({ schedule, added, notAdded }) => {
      await rememberSchedule(queryClient, schedule);
      if (added.length > 0 || notAdded.length > 0) {
        await queryClient.invalidateQueries({ queryKey: scheduleKeys.detail(schedule.id) });
      }
    },
  });
}

/** Answers the schedule and, when a removed store had live products, the release run's id. */
export function useUpdateSchedule(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateProductScheduleRequest) => schedulesApi.update(id, body),
    meta: { successMessage: 'schedules:toasts.updated', silentError: true },
    onSuccess: (change) => {
      const before = queryClient.getQueryData<ProductSchedule>(scheduleKeys.detail(id));
      return rememberSchedule(
        queryClient,
        withoutRelease(change),
        before?.stores.map((s) => s.storeId) ?? [],
      );
    },
  });
}

/**
 * Deletes a draft or ended schedule; with `removeDrafts` the never-published drafts it created
 * go too and the answer counts them. The dialog toasts, since the count is in the message.
 */
export function useDeleteSchedule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, removeDrafts }: { id: string; removeDrafts: boolean }) =>
      schedulesApi.remove(id, { removeDrafts }),
    meta: { silentError: true },
    onSuccess: async (_answer, { id }) => {
      const before = queryClient.getQueryData<ProductSchedule>(scheduleKeys.detail(id));
      queryClient.removeQueries({ queryKey: scheduleKeys.detail(id) });
      await queryClient.invalidateQueries({ queryKey: scheduleKeys.all });
      if (before) {
        await Promise.all(
          before.stores.map((s) =>
            queryClient.invalidateQueries({ queryKey: ['stores', s.storeId, 'catalog'] }),
          ),
        );
      }
    },
  });
}

export type LifecycleAction = 'activate' | 'pause' | 'resume';

const LIFECYCLE_TOAST: Record<LifecycleAction, string> = {
  activate: 'schedules:toasts.activated',
  pause: 'schedules:toasts.paused',
  resume: 'schedules:toasts.resumed',
};

/** Activate (draft), pause (active) or resume (paused); the page shows refusals itself. */
export function useScheduleLifecycle(id: string, action: LifecycleAction) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => schedulesApi[action](id),
    meta: { successMessage: LIFECYCLE_TOAST[action], silentError: true },
    onSuccess: (schedule) => rememberSchedule(queryClient, schedule),
  });
}

/** Ends now: the release run applies `onEnd` and answers its id. */
export function useEndSchedule(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (onEnd: ScheduleOnEndInput) => schedulesApi.end(id, onEnd),
    meta: { successMessage: 'schedules:toasts.ended', silentError: true },
    onSuccess: (change) => rememberSchedule(queryClient, withoutRelease(change)),
  });
}

/** Queues a run outside the cadence (202 with its id); it counts as a cycle. */
export function useRunScheduleNow(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => schedulesApi.runNow(id),
    meta: { successMessage: 'schedules:toasts.runQueued', silentError: true },
    onSuccess: () =>
      refreshSchedule(queryClient, id).then(() =>
        queryClient.invalidateQueries({ queryKey: scheduleKeys.runs(id) }),
      ),
  });
}

/** The next batch as a dry run; nothing is written, so nothing is invalidated. */
export function usePreviewSchedule(id: string) {
  return useMutation({
    mutationFn: () => schedulesApi.preview(id),
    meta: { silentError: true },
  });
}
